import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface CreditAppFillRow {
  id: string;
  load_id: string;
  broker_company_id: string;
  shipper_company_id: string;
  source_template_id: string;
  storage_path: string;
  filled_field_count: number;
  skipped_field_count: number;
  unsupported_types: string[];
  status: "generated" | "failed" | string;
  error_message: string | null;
  generation_source: string;
  generated_by: string | null;
  generated_at: string;
}

export type EligibilityReason =
  | "load_not_found"
  | "load_not_awarded"
  | "no_winning_bid"
  | "winning_bid_not_accepted"
  | "no_active_template"
  | "template_not_fillable"
  | "no_shipper_profile"
  | "shipper_company_missing"
  | "forbidden"
  | "db_error"
  | "template_download_failed"
  | "generation_failed"
  | "storage_failed"
  | "ai_failed"
  | "ai_rate_limited"
  | "ai_credits_exhausted"
  | "unexpected"
  | "load_id_required";

export interface EligibilityResult {
  ok: boolean;
  reason?: EligibilityReason;
  message?: string;
  broker_company_id?: string;
  shipper_company_id?: string;
  template_id?: string;
  field_count?: number;
  function_version?: string;
  shipper_profile_source?: string;
  shipper_profile_updated_at?: string | null;
  shipper_profile_populated_field_count?: number;
  latest_fill_generated_at?: string | null;
  latest_fill_is_stale?: boolean;
}

export function useCreditAppFill(loadId: string | null) {
  const [eligibility, setEligibility] = useState<EligibilityResult | null>(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [fills, setFills] = useState<CreditAppFillRow[]>([]);
  const [fillsLoading, setFillsLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadFills = useCallback(async () => {
    if (!loadId) { setFills([]); return; }
    setFillsLoading(true);
    const { data } = await supabase
      .from("broker_credit_app_fills")
      .select("*")
      .eq("load_id", loadId)
      .order("generated_at", { ascending: false });
    setFills((data as CreditAppFillRow[]) ?? []);
    setFillsLoading(false);
  }, [loadId]);

  const checkEligibility = useCallback(async () => {
    if (!loadId) { setEligibility(null); return; }
    setEligibilityLoading(true);
    const { data, error } = await supabase.functions.invoke("generate-credit-app-fill", {
      body: { load_id: loadId, dry_run: true },
    });
    if (error) {
      setEligibility({ ok: false, reason: "unexpected", message: error.message });
    } else {
      setEligibility(data as EligibilityResult);
    }
    setEligibilityLoading(false);
  }, [loadId]);

  useEffect(() => { checkEligibility(); }, [checkEligibility]);
  useEffect(() => { loadFills(); }, [loadFills]);

  const generate = useCallback(async () => {
    if (!loadId || generating) return { ok: false as const, reason: "busy" as const };
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-credit-app-fill", {
        body: { load_id: loadId },
      });
      if (error) return { ok: false as const, reason: "unexpected" as const, message: error.message };
      const result = data as { ok: boolean; reason?: string; message?: string; fill?: CreditAppFillRow };
      if (result.ok && result.fill) {
        setFills((prev) => [result.fill as CreditAppFillRow, ...prev.filter((f) => f.id !== result.fill!.id)]);
        loadFills();
      }
      return result;
    } finally {
      setGenerating(false);
    }
  }, [loadId, generating, loadFills]);

  const downloadFill = useCallback(async (fill: CreditAppFillRow) => {
    if (!fill.storage_path) return null;
    const { data, error } = await supabase.storage
      .from("credit-app-fills")
      .createSignedUrl(fill.storage_path, 60);
    if (error || !data?.signedUrl) return null;
    return data.signedUrl;
  }, []);

  return {
    eligibility, eligibilityLoading,
    fills, fillsLoading,
    generating, generate,
    refreshEligibility: checkEligibility,
    refreshFills: loadFills,
    downloadFill,
  };
}
